import { useCallback, useLayoutEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { attachQcAxiosAuthInterceptor, qcAxios } from '../api/axiosQcClient';
import { getAuth0TokenAuthorizationParams } from './auth0TokenParams';
import { isQcLiteE2eBypass } from '../utils/qcLiteE2eBypass';

/**
 * Wraps children in Auth0 context and wires an axios request interceptor
 * that injects `Authorization: Bearer <token>` into every QC Lite backend call.
 *
 * Must be rendered inside `Auth0Provider`.
 * Uses `useLayoutEffect` so the interceptor is registered before parent `useEffect`
 * runs (e.g. `WorkflowTagsProvider` bootstrap) — avoids 401 on first API calls.
 *
 * On `missing_refresh_token` / `login_required` / `consent_required` /
 * `invalid_grant`, triggers `loginWithRedirect` once (Auth0 session without RT,
 * or consent needed — silent iframe can't show the consent prompt, e.g. on
 * localhost where Auth0 never skips consent; interactive login can).
 */
export function AuthAxiosProvider({ children }) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const redirectingForReauth = useRef(false);

  const getTokenWithRecovery = useCallback(
    async (config) => {
      try {
        return await getAccessTokenSilently({
          ...config,
          authorizationParams: {
            ...getAuth0TokenAuthorizationParams(),
            ...config?.authorizationParams,
          },
        });
      } catch (e) {
        const code = e?.error;
        if (
          (code === 'missing_refresh_token' ||
            code === 'login_required' ||
            code === 'consent_required' ||
            code === 'invalid_grant') &&
          !redirectingForReauth.current
        ) {
          redirectingForReauth.current = true;
          await loginWithRedirect({
            authorizationParams: getAuth0TokenAuthorizationParams(),
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          });
        }
        throw e;
      }
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  useLayoutEffect(() => {
    if (isQcLiteE2eBypass()) {
      const token =
        import.meta.env.VITE_E2E_MOCK_ACCESS_TOKEN ?? 'e2e-placeholder-token';
      const interceptorId = qcAxios.interceptors.request.use((config) => {
        const next = { ...config };
        next.headers = { ...next.headers };
        next.headers.Authorization = `Bearer ${token}`;
        return next;
      });
      return () => {
        qcAxios.interceptors.request.eject(interceptorId);
      };
    }

    const interceptorId = attachQcAxiosAuthInterceptor(getTokenWithRecovery);

    return () => {
      qcAxios.interceptors.request.eject(interceptorId);
    };
  }, [getTokenWithRecovery]);

  return children;
}
